import { useEffect, useState } from 'react';
import { formatPhoneNumber } from '@stitch/pbx-adapter/catalog';
import { api, type TextView, type TextingView } from '../../api/client';
import { Button, Card, Chip, Empty, Field, LoadError, Loading, Notice, PageHeader, Toast } from '../../components/ui';
import { shortWhen } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';
import { segments, shrinkPicture as shrink } from '../../lib/texting';
import { RegistrationsListCard } from './TextingRegistrationStaff';

export { segments };

/** "+14105550100" from what people type. */
const toE164 = (v: string) => {
  const d = v.replace(/\D/g, '');
  return d.length === 10 ? `+1${d}` : d.length === 11 && d.startsWith('1') ? `+${d}` : '';
};

const STATUS: Record<TextView['status'], { label: string; tone: 'ok' | 'warn' | 'crit' | 'neutral' | 'brand' }> = {
  received: { label: 'Received', tone: 'neutral' },
  sending: { label: 'Sending', tone: 'brand' },
  sent: { label: 'Sent', tone: 'brand' },
  delivered: { label: 'Delivered', tone: 'ok' },
  failed: { label: 'Failed', tone: 'crit' }
};

const CHECKS: { key: keyof TextingView['checklist']; label: string; how: string }[] = [
  { key: 'smsOut', label: 'An SMS sent and delivered', how: 'Send one to your mobile below; it ticks when the carrier’s receipt says delivered.' },
  { key: 'smsIn', label: 'An SMS received', how: 'Text one of Stitch’s numbers from your mobile.' },
  { key: 'mmsOut', label: 'An MMS sent', how: 'Send a picture to your mobile below.' },
  { key: 'mmsIn', label: 'An MMS received, with its picture kept', how: 'Text a photo to one of Stitch’s numbers.' },
  { key: 'receipts', label: 'Delivery receipts arriving', how: 'Set the receipts address in Ahoi’s portal (below).' },
  { key: 'secured', label: 'Webhooks locked to the carrier’s addresses', how: 'Once you’ve seen where Ahoi calls from, add those addresses on System → Carrier.' },
  { key: 'rejectsOthers', label: 'A call from anywhere else turned away', how: 'Post to the inbound address from another network, like a laptop on Wi-Fi: it should show as turned away.' }
];

/**
 * Stitch staff check texting through the carrier end to end (Phase 0 of Stitch Communications):
 * send and receive SMS and MMS, watch delivery receipts arrive, and lock the webhooks down.
 */
export function AdminTexting() {
  const data = useData(() => api.texting(), []);
  const [toast, setToast] = useState('');

  // Texts and receipts arrive from the carrier: look again every few seconds while open.
  const { reload } = data;
  useEffect(() => {
    const t = window.setInterval(() => document.visibilityState === 'visible' && reload(), 5000);
    return () => window.clearInterval(t);
  }, [reload]);

  const v = data.data;
  const done = v ? CHECKS.filter((c) => v.checklist[c.key]).length : 0;
  return (
    <>
      <PageHeader eyebrow={v ? `${done} of ${CHECKS.length} checked` : 'Stitch Admin'} title="Texting check" />
      <div className="content">
        {data.error ? (
          <Card>
            <LoadError error={data.error} retry={data.reload} />
          </Card>
        ) : !v ? (
          <Loading label="Loading texting" />
        ) : (
          <>
            <RegistrationsListCard />
            <div className="texting-grid">
              <Card title="Checklist">
                <ul className="setup-list card-body">
                  {CHECKS.map((c) => (
                    <li key={c.key} className={v.checklist[c.key] ? 'ok' : 'todo'}>
                      <Icon name={v.checklist[c.key] ? 'check' : 'alert'} size={16} strokeWidth={2.3} />
                      <span className="cell-stack">
                        <span>{c.label}</span>
                        {!v.checklist[c.key] && <small className="muted">{c.how}</small>}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
              <Setup v={v} onCopied={setToast} />
            </div>
            <SendCard v={v} onSent={(m) => (setToast(m), data.reload())} />
            <Card title={`Texts · ${v.texts.length}`}>
              {v.texts.length === 0 ? (
                <Empty title="No texts yet">Send one above, or text one of Stitch’s numbers.</Empty>
              ) : (
                <ul className="text-log">
                  {v.texts.map((t) => (
                    <TextRow key={t.id} t={t} />
                  ))}
                </ul>
              )}
            </Card>
            <Card title="Webhook calls">
              {v.hooks.length === 0 ? (
                <Empty title="None yet">Every call to the texting addresses shows here, turned away or not.</Empty>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">When</th>
                        <th scope="col">What</th>
                        <th scope="col">From</th>
                        <th scope="col">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {v.hooks.map((h) => (
                        <tr key={h.id}>
                          <td>{shortWhen(h.at)}</td>
                          <td>{h.kind === 'receipt' ? 'Delivery receipt' : 'Text in'}</td>
                          <td className="mono">{h.ip || '—'}</td>
                          <td>
                            <span className="cell-stack">
                              <Chip small tone={h.accepted ? 'ok' : 'crit'}>
                                {h.accepted ? 'Taken' : 'Turned away'}
                              </Chip>
                              {h.reason && <small className="muted">{h.reason}</small>}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
      </div>
      {toast && <Toast tone="ok" message={toast} onDismiss={() => setToast('')} />}
    </>
  );
}

function Setup({ v, onCopied }: { v: TextingView; onCopied: (m: string) => void }) {
  const copy = async (what: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      onCopied(`${what} copied.`);
    } catch {
      onCopied('Couldn’t copy: select it and copy it yourself.');
    }
  };
  const w = v.webhooks;
  return (
    <Card title="Setup">
      <div className="card-body section-stack">
        <ul className="setup-list">
          <li className={v.carrier ? 'ok' : 'todo'}>
            <Icon name={v.carrier ? 'check' : 'alert'} size={16} strokeWidth={2.3} />
            <span className="cell-stack">
              <span>{v.carrier ? `Carrier: ${v.carrier.name}` : 'No carrier connected'}</span>
              {!v.carrier && <small className="muted">Add the carrier’s API token on System → Carrier accounts.</small>}
            </span>
          </li>
          <li className={v.carrier?.writes ? 'ok' : 'todo'}>
            <Icon name={v.carrier?.writes ? 'check' : 'alert'} size={16} strokeWidth={2.3} />
            <span className="cell-stack">
              <span>{v.carrier?.writes ? 'Sending is on' : 'Sending is off'}</span>
              {!v.carrier?.writes && <small className="muted">Texts cost money: turn Spending on, on System → Carrier, to send.</small>}
            </span>
          </li>
          <li className={w.on && v.publicUrl ? 'ok' : 'todo'}>
            <Icon name={w.on && v.publicUrl ? 'check' : 'alert'} size={16} strokeWidth={2.3} />
            <span className="cell-stack">
              <span>{!w.on ? 'Webhooks are off' : !v.publicUrl ? 'Webhooks are on, without an address yet' : 'Webhooks are on'}</span>
              {!w.on && <small className="muted">Make a webhook secret on System → Carrier{v.publicUrl ? '' : ', and set PUBLIC_URL'}.</small>}
              {w.on && !v.publicUrl && <small className="muted">Set PUBLIC_URL, the portal’s address: the webhook addresses and MMS picture links are built from it.</small>}
            </span>
          </li>
          <li className={w.ips.length ? 'ok' : 'todo'}>
            <Icon name={w.ips.length ? 'check' : 'alert'} size={16} strokeWidth={2.3} />
            <span className="cell-stack">
              <span>{w.ips.length ? `Only from ${w.ips.join(', ')}` : 'From any address, for now'}</span>
              {!w.ips.length && (
                <small className="muted">
                  {w.seenIps.length ? `Seen so far: ${w.seenIps.join(', ')}. ` : ''}Add Ahoi’s addresses on System → Carrier once you know them.
                </small>
              )}
            </span>
          </li>
        </ul>
        {!w.trustProxy && <Notice tone="warn">TRUST_PROXY isn’t set, so behind EasyPanel every call looks like it comes from the proxy. Set TRUST_PROXY=1 so the address check sees Ahoi’s.</Notice>}
        {w.inboundUrl && w.receiptUrl && (
          <div className="hook-urls">
            <span className="field-label">In Ahoi’s portal (User Settings)</span>
            <HookUrl label="Incoming SMS/MMS URL" value={w.inboundUrl} onCopy={() => void copy('The incoming address', w.inboundUrl!)} />
            <HookUrl label="Receive SMS/MMS Delivery Messages URL" value={w.receiptUrl} onCopy={() => void copy('The receipts address', w.receiptUrl!)} />
            <small className="muted">These hold the secret: share them only with Ahoi.</small>
          </div>
        )}
        <small className="muted">Pictures are kept in {v.storage === 'b2' ? 'Stitch Block Storage' : 'the database'}.</small>
      </div>
    </Card>
  );
}

function HookUrl({ label, value, onCopy }: { label: string; value: string; onCopy: () => void }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="hook-url">
      <span className="cell-stack grow">
        <small className="muted">{label}</small>
        <span className="mono">{shown ? value : value.replace(/(\/hooks\/ahoi\/)[^/]+/, '$1••••••')}</span>
      </span>
      <Button size="small" variant="ghost" onClick={() => setShown((s) => !s)}>
        {shown ? 'Hide' : 'Show'}
      </Button>
      <Button size="small" icon="copy" onClick={onCopy}>
        Copy
      </Button>
    </div>
  );
}

function SendCard({ v, onSent }: { v: TextingView; onSent: (message: string) => void }) {
  const [from, setFrom] = useState(v.numbers[0] ?? '');
  const [to, setTo] = useState('');
  const [text, setText] = useState('');
  const [image, setImage] = useState<{ name: string; url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const seg = segments(text);
  const target = toE164(to);
  const canSend = Boolean(v.carrier?.writes && from && target && (text.trim() || image) && !busy);

  return (
    <Card title="Send a test text">
      <div className="card-body section-stack">
        {!v.numbers.length && <Notice>No numbers at the carrier to send from yet.</Notice>}
        <div className="form-grid">
          <div className="field">
            <label htmlFor="tx-from">From</label>
            <select id="tx-from" className="input" value={from} onChange={(e) => setFrom(e.target.value)}>
              {v.numbers.map((n) => (
                <option key={n} value={n}>
                  {formatPhoneNumber(n)}
                </option>
              ))}
            </select>
          </div>
          <Field label="To" type="tel" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Your mobile" error={to && !target ? 'A US or Canadian number.' : undefined} />
        </div>
        <div className="field stretch">
          <label htmlFor="tx-text">Message</label>
          <textarea id="tx-text" className="input textarea" rows={3} value={text} maxLength={1600} onChange={(e) => setText(e.target.value)} />
          <span className="hint">
            {seg.count === 0 ? 'Empty' : `${seg.count} ${seg.count === 1 ? 'segment' : 'segments'} · ${seg.used} of ${seg.count * seg.perSegment}`}
            {seg.encoding === 'ucs2' && text ? ' · an emoji or special character makes each segment 70 characters' : ''}
          </span>
        </div>
        <div className="field">
          <label htmlFor="tx-pic">Picture (sends as an MMS)</label>
          <input
            id="tx-pic"
            type="file"
            accept="image/jpeg,image/png,image/gif"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              setError('');
              if (!f) return setImage(null);
              try {
                setImage({ name: f.name, url: await shrink(f) });
              } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
              }
            }}
          />
          {image && (
            <span className="text-pic-preview">
              <img src={image.url} alt="" />
              <button type="button" className="link-btn" onClick={() => setImage(null)}>
                Remove
              </button>
            </span>
          )}
        </div>
        <div className="wizard-actions">
          <Button
            variant="primary"
            icon="send"
            disabled={!canSend}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                const r = await api.sendText({ from, to: target, text, ...(image ? { image: image.url } : {}) });
                if (r.text.status === 'failed') setError(r.text.error ?? 'The carrier didn’t take it.');
                else {
                  onSent(`${image ? 'MMS' : 'SMS'} sent to ${formatPhoneNumber(target)}.`);
                  setText('');
                  setImage(null);
                }
              } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Sending…' : image ? 'Send MMS' : 'Send SMS'}
          </Button>
          {!v.carrier?.writes && <span className="muted">Sending is off (Spending, on System → Carrier).</span>}
        </div>
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
    </Card>
  );
}

function TextRow({ t }: { t: TextView }) {
  const [open, setOpen] = useState(false);
  const s = STATUS[t.status];
  const out = t.direction === 'out';
  return (
    <li className={`text-row ${t.direction}`}>
      <span className="text-dir" aria-hidden="true">
        <Icon name={out ? 'arrowOut' : 'arrowIn'} size={16} strokeWidth={2.2} />
      </span>
      <div className="text-main">
        <div className="text-head">
          <span className="mono">
            {formatPhoneNumber(out ? t.stitchNumber : t.otherNumber)} → {formatPhoneNumber(out ? t.otherNumber : t.stitchNumber)}
          </span>
          <Chip small tone={s.tone}>
            {s.label}
          </Chip>
          {t.kind === 'mms' && <Chip small>MMS</Chip>}
          <span className="muted small-print">
            {shortWhen(t.createdAt)}
            {t.sentByName ? ` · ${t.sentByName}` : ''}
            {t.tenant ? ` · ${t.tenant}` : ''}
          </span>
        </div>
        {t.body && <p className="text-body">{t.body}</p>}
        {t.media.length > 0 && (
          <div className="text-media">
            {t.media.map((m) =>
              m.stored === 'none' ? (
                <span key={m.seq} className="muted small-print">
                  Picture {m.seq + 1} not kept: {m.error}
                </span>
              ) : m.contentType.startsWith('image/') ? (
                <a key={m.seq} href={api.textMediaUrl(t.id, m.seq)} target="_blank" rel="noreferrer">
                  <img src={api.textMediaUrl(t.id, m.seq)} alt={`Picture ${m.seq + 1}`} />
                </a>
              ) : (
                <a key={m.seq} href={api.textMediaUrl(t.id, m.seq)} target="_blank" rel="noreferrer">
                  {m.contentType} · {Math.round(m.bytes / 1024)} KB
                </a>
              )
            )}
          </div>
        )}
        {t.error && <small className="error">{t.error}</small>}
        {t.receipts.length > 0 && (
          <button type="button" className="link-btn small-print" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            {open ? 'Hide' : 'Show'} {t.receipts.length === 1 ? 'the receipt' : `${t.receipts.length} receipts`}
          </button>
        )}
        {open && (
          <ol className="receipt-list">
            {t.receipts.map((r, i) => (
              <li key={i}>
                <span className="muted">{shortWhen(r.at)}</span> <span className="mono">{[r.sendStatus, r.status, r.error].filter(Boolean).join(' · ')}</span>
              </li>
            ))}
          </ol>
        )}
        {out && t.kind === 'sms' && !t.carrierUuid && t.status !== 'failed' && <small className="muted">The carrier gave no uuid, so receipts can’t be matched to this one.</small>}
      </div>
    </li>
  );
}
